import { currentLang, t } from './i18n';
import {
  currentUser, currentSearchQuery, currentMainTab, setCurrentMainTab,
  setCurrentDeadlineFilter, setCurrentStatusFilter,
  cachedAllGoals, pendingSubtaskGoalIds,
  currentStatusFilter, setDoneGoalsLimit, DONE_GOALS_PAGE_SIZE,
} from './state';
import { api } from './api';
import { showToast, showToastWithAction } from './toast';
import { escapeHtml } from './html-escape';
import { loadAllGoals, startSubtaskPolling } from './upload';
import { clearSearch } from './filters';
import { renderAll } from './goals';
import { loadProfileForSettings } from './profile';
import { classifyInviteAcceptHash } from './invite-accept-logic';
import { markMainTab } from './app-nav';
import { loadWorkList, openWorkDetail, renderWorkList, revealOpenWorkRow, workListState } from './work-list';
import { renderFilterBar } from './filters';

/**
 * #2856: 今日 and すべて are the same list (rows due by today, or any date),
 * and 完了 is the goal cards of the goals already done.
 */
export function switchMainTab(tab: string) {
  setCurrentMainTab(tab);
  if (currentSearchQuery) clearSearch();
  markMainTab(tab);
  document.querySelectorAll('.panel').forEach(el => el.classList.remove('active'));
  const panelId = { today: 'panelToday', all: 'panelToday', done: 'panelAll', create: 'panelCreate' }[tab];
  document.getElementById(panelId).classList.add('active');

  if (tab === 'today' || tab === 'all') {
    workListState.range = tab;
    renderWorkList();
    loadAllGoals({ poll: true });
  }
  if (tab === 'done') {
    if (currentStatusFilter !== 'done') {
      setCurrentStatusFilter('done');
      setDoneGoalsLimit(DONE_GOALS_PAGE_SIZE);
    }
    renderFilterBar();
    renderAll();
    loadAllGoals();
  }

  const fab = document.getElementById('createFab');
  if (fab && currentUser) {
    fab.style.display = tab === 'create' ? 'none' : 'flex';
  }
}

export function switchInputMode(mode: string) {
  const modes = ['screenshot', 'text'];
  document.querySelectorAll('.input-mode-btn').forEach((btn, i) => {
    btn.classList.toggle('active', modes[i] === mode);
  });
  document.getElementById('modeScreenshot').classList.toggle('active', mode === 'screenshot');
  document.getElementById('modeText').classList.toggle('active', mode === 'text');
}

export async function handleTextAnalyze() {
  const text = (document.getElementById('textTaskInput') as HTMLTextAreaElement).value.trim();
  if (!text) { showToast(t('textEmpty') as string, 'info'); return; }

  const btn = document.getElementById('textAnalyzeBtn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner spinner-white"></span> 生成中...';

  const resultArea = document.getElementById('textResultArea');
  resultArea.innerHTML = '';

  try {
    const body = { text };

    const data = await api('/api/analyze-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const goals = data.goals || [];
    const notice = data.intake_notice;
    const noticeText = notice?.question || notice?.reason || '';
    const summary = data.ai_summary || noticeText;
    if (data._recaptured) {
      const recapturedGoalId = data._recaptured_goal_id || goals[0]?.id || null;
      const message = currentLang === 'en'
        ? 'Updated an existing goal instead of creating a duplicate.'
        : '重複ゴールは作らず、既存ゴールを更新しました。';
      if (recapturedGoalId) {
        showToastWithAction(message, currentLang === 'en' ? 'Open goal' : 'ゴールを開く', () => {
          void navigateToGoal(recapturedGoalId);
        });
      } else {
        showToast(message, 'info', 6000);
      }
    }

    let html = '';
    if (summary) {
      html += `<div class="upload-file-summary" style="margin-top:12px;">${escapeHtml(summary)}</div>`;
    }
    if (!summary && goals.length === 0 && noticeText) {
      html += `<div class="upload-file-summary" style="margin-top:12px;">${escapeHtml(noticeText)}</div>`;
    }
    if (goals.length > 0) {
      html += '<div class="upload-goal-links" style="margin-top:10px;">';
      goals.forEach(g => {
        html += `<button class="upload-goal-link" data-navigate-goal-id="${g.id}"><span class="mi mi-xs">flag</span> ${escapeHtml(g.title)}</button>`;
      });
      html += '</div>';
    }
    resultArea.innerHTML = html;

    for (const g of goals) {
      if ((!g.subtasks || g.subtasks.length === 0) && (!g.decisions || g.decisions.length === 0)) {
        pendingSubtaskGoalIds.add(g.id);
      }
    }

    (document.getElementById('textTaskInput') as HTMLTextAreaElement).value = '';
    loadAllGoals();
    startSubtaskPolling();
  } catch (err) {
    showToast('生成エラー: ' + err.message);
  }

  (btn as HTMLButtonElement).disabled = false;
  btn.innerHTML = '<span class="mi mi-sm">auto_awesome</span> AIでタスク生成';
}

export function openCreateFab() {
  switchMainTab('create');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Opens a goal (or one of its tasks) in the detail beside すべて, with its row
 * marked open and scrolled into view, as when opened from the extension.
 */
async function openInList(goalId: string, subtaskId: string | null) {
  // Opened from a link the list may not have arrived yet; without it no row is marked.
  if (!workListState.list) await loadWorkList();
  const items = workListState.list?.items ?? [];
  if (subtaskId) {
    const row = items.find((item) => item.subtask_id === subtaskId);
    await openWorkDetail({ type: 'task', goalId, subtaskId, key: row?.key ?? null });
  } else {
    // The goal's own row, when it has one; else its first row (a goal waiting
    // on the other side has only its wait row).
    const rows = items.filter((item) => item.goal_id === goalId && !item.candidate_id);
    const row = rows.find((item) => !item.subtask_id) ?? rows[0];
    await openWorkDetail({ type: 'goal', goalId, key: row?.key ?? null });
  }
  await revealOpenWorkRow();
}

/** Highlights a done goal's card under 完了. */
function flashDoneGoal(goalId: string) {
  const el = document.querySelector('#panelAll #goal_' + goalId) as HTMLElement | null;
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.style.transition = 'box-shadow 0.3s ease';
  el.style.boxShadow = '0 0 0 3px var(--accent)';
  setTimeout(() => { el.style.boxShadow = ''; }, 2000);
}

export async function navigateToGoal(goalId: string) {
  pendingSubtaskGoalIds.delete(goalId);

  const done = cachedAllGoals.find(g => g.id === goalId)?.status === 'done';
  switchMainTab(done ? 'done' : 'all');
  await loadAllGoals();
  if (done) flashDoneGoal(goalId);
  else await openInList(goalId, null);
}

// Opens the accept screen when the address carries an invitation token. It
// runs once the session exists (enterApp, or after onboarding for a new
// account), so a logged-out invitee lands here after signing in.
export async function openInviteAcceptFromHash(): Promise<boolean> {
  const token = classifyInviteAcceptHash(window.location.hash);
  if (!token) return false;
  await customElements.whenDefined('zen-invite-accept');
  const el = document.getElementById('inviteAccept') as (HTMLElement & { open?: (token: string) => void }) | null;
  el?.open?.(token);
  return true;
}

export function handleHashNavigation() {
  const hash = window.location.hash;
  if (!hash) return;
  if (classifyInviteAcceptHash(hash)) {
    void openInviteAcceptFromHash();
    return;
  }
  if (hash === '#profile') {
    history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#settings-profile`,
    );
    window.dispatchEvent(new Event('hashchange'));
    return;
  }
  if (hash === '#settings' || hash.startsWith('#settings-')) {
    void loadProfileForSettings();
    return;
  }
  if (!hash.startsWith('#goal_')) return;

  const subtaskMatch = hash.match(/^#goal_(.+)_subtask_(.+)$/);
  const goalId = subtaskMatch ? subtaskMatch[1] : hash.replace('#goal_', '');
  const subtaskId = subtaskMatch ? subtaskMatch[2] : null;

  const doScroll = () => {
    const goalData = cachedAllGoals.find(g => g.id === goalId);
    if (!goalData) return;
    if (goalData.status !== 'done') {
      // #2856: an open goal opens in the detail beside すべて.
      if (currentMainTab !== 'all') switchMainTab('all');
      void openInList(goalId, subtaskId);
      return;
    }
    if (currentMainTab !== 'done') switchMainTab('done');
    setCurrentDeadlineFilter('all');
    renderFilterBar();
    renderAll();
    flashDoneGoal(goalId);
  };
  if (document.visibilityState === 'visible') {
    setTimeout(doScroll, 100);
  } else {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisible);
        setTimeout(doScroll, 100);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
  }
  history.replaceState(null, '', window.location.pathname);
}

// Settings shell (#1473) opens/closes purely via hashchange (see zen-settings-page).
// Keep the profile draft fresh whenever the user navigates into #settings while the app is running.
export function initSettingsHashListener() {
  let wasInSettings = window.location.hash === '#settings' || window.location.hash.startsWith('#settings-');
  window.addEventListener('hashchange', () => {
    const isInSettings = window.location.hash === '#settings' || window.location.hash.startsWith('#settings-');
    // Only reload profile draft on transition into settings (not on intra-settings nav).
    if (isInSettings && !wasInSettings) void loadProfileForSettings();
    wasInSettings = isInSettings;
  });
}
